import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { MDBRow, MDBCol } from "mdbreact";
import moment from "moment/moment";

import "../CIIData.scss";

import { Button, Input, Label } from "abs-ui-components";

import { CII_DATA } from "../CIIDataConstants";
import { getCIIdata, confirmManualCIIUpdate, fetchManualCII } from "../../../../../services/CIIData-service.js";
import { emitToasterMsg } from "../../../../../services/toasterUtility.js";
import { preventScientificNotation, preventSpecialCharacters, roundOff } from "../../../../../services/utilities.js";
import { accessAPIForResponse } from "../../../../../services/api-services/api-implementations.js";

const CIIExclusionCalculation = ({ isVesselEmissionOverviewCII }) => {
	const {
		control,
		setValue,
		getValues,
		formState: { errors },
		handleSubmit,
		reset,
	} = useForm();
	const [CIIData, setCIIData] = useState([]);
	const [lastVoyageDate, setLastVoyageDate] = useState("");
	const [loader, setLoader] = useState(false);
	const reportDetails = useSelector((state) => state?.reports?.selectedReportDetails);
	const [reportFlags, setReportFlags] = useState(null);

	const ciiExclusion = getValues("ciiExclusion");

	const handleForm = async () => {
		const date = moment(lastVoyageDate, "DD MMM YYYY");

		// Set the time to 11:59:59
		date.set({ hour: 11, minute: 59, second: 59 });

		// Format the date with time
		const formattedDate = date.format("MM-DD-YYYY HH:mm:ss");
		const confirmManualCIIUpdateObj = {
			attainedCIIWithoutExclusion: Number(
				ciiExclusion?.attainedCIIWithoutExclusion ?? 0
			).toFixed(3),
			attainedCIIWithExclusion: Number(
				ciiExclusion?.attainedCIIWithExclusion ?? 0
			).toFixed(3),
			attainedRequiredCII: Number(
				+ciiExclusion?.requiredCII ?? 0
			).toFixed(3),
			attainedCIIGradeWithoutExclusion: "",
			attainedCIIGradeWithExclusion: "",
			lastVoyageDate: formattedDate,
		};

		try {
			const response = await confirmManualCIIUpdate(reportDetails.ReportMstId, confirmManualCIIUpdateObj);

			let manualCIIGrade = response;
			if (manualCIIGrade) {
				getInitialManualCiiData(reportDetails.ReportMstId);
				emitToasterMsg("success", "CII Data", "CII calculation completed.");
			}
		} catch (error) {
			emitToasterMsg("error", error.response.data.errorMessage);
		}
	};

	const GetReportFlagsByStatus = async (reportMasterId) => {
		await accessAPIForResponse("GetReportFlags", null, [reportMasterId]).then((response) => {
			setReportFlags(response);
		});
	};

	const handleClearCIIManualForm = async () => {
		// Manually set values to 0 (to ensure form state is updated correctly)
		setValue("ciiExclusion.requiredCII", 0);
		setValue("ciiExclusion.attainedCIIWithoutExclusion", 0);
		setValue("ciiExclusion.attainedCIIWithExclusion", 0);
		setValue("ciiExclusion.gradeWithoutExclusion", "");
		setValue("ciiExclusion.gradeWithExclusion", "");

		// Reset the form
		reset({
			ciiExclusion: {
				requiredCII: 0,
				attainedCIIWithoutExclusion: 0,
				attainedCIIWithExclusion: 0,
				gradeWithoutExclusion: "A",
				gradeWithExclusion: "A",
			},
		});

		// Persist the cleared state (send it to the API)
		const clearedFormObj = {
			attainedCIIWithoutExclusion: 0,
			attainedCIIWithExclusion: 0,
			requiredCII: 0,
			attainedCIIGradeWithoutExclusion: "X",
			attainedCIIGradeWithExclusion: "X",
		};

		try {
			// Make an API call to update the cleared values on the server
			await confirmManualCIIUpdate(reportDetails.ReportMstId, clearedFormObj);
		} catch (error) {
			console.error("Error clearing form on the server:", error);
		}

		// Optionally, persist the cleared state locally as well
		localStorage.setItem("ciiExclusionData", JSON.stringify(clearedFormObj));
	};

	const getInitialManualCiiData = async (reportMstId) => {
		try {
			const response = await fetchManualCII(reportMstId);
			if (response) {
				let manualCIIGrade = response.data;
				setValue("ciiExclusion.requiredCII", manualCIIGrade.requiredCII);
				setValue("ciiExclusion.attainedCIIWithoutExclusion", manualCIIGrade.ciiWithoutExclusion);
				setValue("ciiExclusion.attainedCIIWithExclusion", manualCIIGrade.ciiWithExclusion);
				setValue("ciiExclusion.gradeWithExclusion", manualCIIGrade.ciiGradeWithExclusion);
				setValue("ciiExclusion.gradeWithoutExclusion", manualCIIGrade.ciiGradeWithoutExclusion);
			}
		} catch (err) {
			console.error(err);
		}
	};
	//future scope
	useEffect(() => {
		GetReportFlagsByStatus(reportDetails.ReportMstId);
		getInitialManualCiiData(reportDetails.ReportMstId);
	}, []);

	useEffect(() => {
		const fetchData = async () => {
			try {
				const savedData = localStorage.getItem("ciiExclusionData");
				if (!savedData || JSON.parse(savedData).requiredCII === "0") {
					const apiData = await getInitialManualCiiData(reportDetails.ReportMstId);
					reset(apiData);
				} else {
					reset(
						JSON.parse(savedData) || {
							ciiExclusion: {
								requiredCII: "0",
								attainedCIIWithoutExclusion: "0",
								attainedCIIWithExclusion: "0",
								gradeWithoutExclusion: "A",
								gradeWithExclusion: "A",
							},
						}
					);
				}
			} catch (error) {
				console.error("Error fetching CII data", error);
			}
		};

		fetchData();
	}, [reportDetails.ReportMstId, reset]);

	useEffect(() => {
		const fetchCIIData = async () => {
			setLoader(true);
			try {
				const response = await getCIIdata(reportDetails.ReportMstId);
				let voyageDate = response?.data?.lastVoyageDate;
				setCIIData(response?.data || []);
				let dateInfo = voyageDate instanceof Date ? moment(voyageDate).format("DD MMM YYYY") : voyageDate;
				setLastVoyageDate(dateInfo);
			} catch (error) {
				console.error("Error fetching data:", error);
			} finally {
				setLoader(false);
			}
		};

		fetchCIIData();
	}, []);

	return (
		<div className="ciidata-exclusions-container">
			<Label className="mb-1 cii-exclusion-label cii-exclusion" bold name={CII_DATA.CII_EXCLUSIONS_HEADER} />
			<MDBRow className="col-12 pt-4 d-flex cii-exclusion">
				<MDBCol className="col-7 pl-0">
					<Label className="cii-exclusion-label" name={`${CII_DATA.REQUIRED_CII_TEXT}`} />
				</MDBCol>
				<MDBCol className="col-3 ">
					<Input
						id="attaines_required_cii"
						value={
							CIIData.attainedRequiredCII === -1
								? "Not Applicable"
								: CIIData.attainedRequiredCII != null && CIIData.attainedRequiredCII !== undefined
								? roundOff(CIIData.attainedRequiredCII, 3, 3)
								: 0.000
						}
						disabled
						type="text"
						readOnly
						className="readonly-input"
					/>
				</MDBCol>
			</MDBRow>
			<MDBRow className="col-12 pt-3 cii-exclusion">
				<MDBCol className="col-7  pl-0">
					<Label className="cii-exclusion-label" name={`${CII_DATA.ATTAINED_WITHOUT_EXCLUSIONS_TEXT} ${lastVoyageDate} `} />
				</MDBCol>
				<MDBCol className="col-3">
					<Input
						id="attained_without_exclusions"
						disabled
						name={`${CII_DATA.ATTAINED_WITHOUT_EXCLUSIONS_TEXT}`}
						value={
							CIIData.attainedCIIWithoutExclusion == -1
								? "Not Applicable"
								: CIIData.attainedCIIWithoutExclusion != null && CIIData.attainedCIIWithoutExclusion !== undefined
								? roundOff(CIIData.attainedCIIWithoutExclusion, 3, 3)
								: 0.000
						}
						type="text"
						readOnly
						className="readonly-input"
					/>
				</MDBCol>
			</MDBRow>
			<MDBRow className="col-12 pt-3 cii-exclusion">
				<MDBCol className="col-7  pl-0">
					<Label className="cii-exclusion-label" name={`${CII_DATA.ATTAINED_WITH_EXCLUSIONS_TEXT} ${lastVoyageDate}`} />
				</MDBCol>
				<MDBCol className="col-3">
					<Input
						id="attained_with_exclusions"
						value={
							CIIData.attainedCIIWithExclusion == -1
								? "Not Applicable"
								: CIIData.attainedCIIWithExclusion != null && CIIData.attainedCIIWithExclusion !== undefined
								? roundOff(CIIData.attainedCIIWithExclusion, 3, 3)
								: 0.000
						}
						disabled
						type="text"
						readOnly
						className="readonly-input"
					/>
				</MDBCol>
			</MDBRow>
			<MDBRow className="col-12 pt-3 cii-exclusion">
				<MDBCol className="col-7  pl-0">
					<Label className="cii-exclusion-label" name={`${CII_DATA.GRADES_WITHOUT_EXCLUSIONS_TEXT} ${lastVoyageDate}`} />
				</MDBCol>
				<MDBCol className="col-3">
					<Input
						id="attained_grade_without_exclusions"
						value={CIIData.attainedCIIGradeWithoutExclusion}
						disabled
						type="text"
						readOnly
						className="readonly-input"
					/>
				</MDBCol>
			</MDBRow>
			<MDBRow className="col-12 pt-3 cii-exclusion">
				<MDBCol className="col-7  pl-0">
					<Label className="cii-exclusion-label" name={`${CII_DATA.GRADES_WITH_EXCLUSIONS_TEXT} ${lastVoyageDate}`} />
				</MDBCol>
				<MDBCol className="col-3">
					<Input
						id="attained_grade_with_exclusions"
						value={CIIData.attainedCIIGradeWithExclusion}
						disabled
						type="text"
						readOnly
						className="readonly-input"
					/>
				</MDBCol>
			</MDBRow>
			{!isVesselEmissionOverviewCII && (
				<div>
					<Label bold name={CII_DATA.CLIENT_EXCLUSIONS_HEADER} className="mt-4 mb-3 cii-exclusion" />
					<form onSubmit={handleSubmit(handleForm)}>
						<MDBRow className="col-12 pt-3 cii-exclusion">
							<MDBCol className="col-7 p-0">
								<Label
									required
									className="cii-exclusion-label"
									name={`${CII_DATA.REQUIRED_CII_TEXT} ${lastVoyageDate}
									${CII_DATA.INPUT_CLIENT}`}
								/>
							</MDBCol>
							<MDBCol className="col-3">
								<Controller
									name="ciiExclusion.requiredCII"
									control={control}
									rules={{
										required: "CII of Vessel is required",
										setValueAs: (value) =>
											value === "" ? "" : Number(value).toFixed(3),
									}}
									render={({ field }) => (
										<div>
											<Input
												id="required_cii_input"
												disabled={!reportFlags?.VoyagesEnabled}
												onChange={(e) => {
													field.onChange(e);
												}}
												onKeyDown={(e) => {
													preventSpecialCharacters(e);
													// Prevent up and down arrow keys
													if (e.key === "ArrowUp" || e.key === "ArrowDown") {
														e.preventDefault();
													}
												}}
												type="number"
												step="0.001"
												value={field.value}
												autoFocus={false}
												className="cii-input"
												onWheel={(e) => e.target.blur()}
											/>
										</div>
									)}
								/>
								<div className="mt-1">
									{errors?.ciiExclusion?.requiredCII && <div className="error">{errors?.ciiExclusion?.requiredCII?.message}</div>}
								</div>
							</MDBCol>
						</MDBRow>
						<MDBRow className="col-12 pt-3 cii-exclusion">
							<MDBCol className="col-7 p-0">
								<Label
									required
									className="cii-exclusion-label"
									name={`${CII_DATA.ATTAINED_WITHOUT_EXCLUSIONS_TEXT} ${lastVoyageDate}
									${CII_DATA.INPUT_CLIENT}`}
								/>
							</MDBCol>
							<MDBCol className="col-3">
								<Controller
									name="ciiExclusion.attainedCIIWithoutExclusion"
									control={control}
									rules={{
										required: "Estimated Attained CII without exclusion is required",
										setValueAs: (value) =>
											value === "" ? "" : Number(value).toFixed(3),
									}}
									render={({ field }) => (
										<div>
											<Input
												required
												disabled={!reportFlags?.VoyagesEnabled}
												id="attained_cii_without_exclusions_input"
												onChange={(e) => {
													field.onChange(e);
												}}
												onKeyDown={(e) => {
													preventScientificNotation(e);
													// Prevent up and down arrow keys
													if (e.key === "ArrowUp" || e.key === "ArrowDown") {
														e.preventDefault();
													}
												}}
												type="number"
												step="0.001"
												value={field.value}
												autoFocus={false}
												className="cii-input"
												onWheel={(e) => e.target.blur()}
											/>
										</div>
									)}
								/>
								<div className="mt-1">
									{errors?.ciiExclusion?.attainedCIIWithoutExclusion && (
										<div className="error">{errors?.ciiExclusion?.attainedCIIWithoutExclusion?.message}</div>
									)}
								</div>
							</MDBCol>
						</MDBRow>
						<MDBRow className="col-12 pt-3 cii-exclusion">
							<MDBCol className="col-7 p-0">
								<Label
									required
									className="cii-exclusion-label"
									name={`${CII_DATA.ATTAINED_WITH_EXCLUSIONS_TEXT} ${lastVoyageDate}
									${CII_DATA.INPUT_CLIENT}`}
								/>
							</MDBCol>
							<MDBCol className="col-3">
								<Controller
									name="ciiExclusion.attainedCIIWithExclusion"
									control={control}
									rules={{
										required: "Estimated Attained CII with exclusion is required",
										 setValueAs: (value) =>
										 	value === "" ? "" : Number(value).toFixed(3),
									}}
									render={({ field }) => (
										<div>
											<Input
												required
												disabled={!reportFlags?.VoyagesEnabled}
												id="attained_cii_with_exclusions_input"
												onChange={(e) => {
													field.onChange(e);
												}}
												type="number"
												onKeyDown={(e) => {
													preventScientificNotation(e);
													// Prevent up and down arrow keys
													if (e.key === "ArrowUp" || e.key === "ArrowDown") {
														e.preventDefault();
													}
												}}
												step="0.001"
												value={field.value}
												autoFocus={false}
												className="cii-input"
												onWheel={(e) => e.target.blur()}
											/>
										</div>
									)}
								/>
								<div className="mt-1">
									{errors?.ciiExclusion?.attainedCIIWithExclusion && (
										<div className="error">{errors?.ciiExclusion?.attainedCIIWithExclusion?.message}</div>
									)}
								</div>
							</MDBCol>
						</MDBRow>
						<MDBRow className="col-12 pt-3 cii-exclusion">
							<MDBCol className="col-7 p-0">
								<Label
									className="cii-exclusion-label"
									name={`${CII_DATA.GRADES_WITHOUT_EXCLUSIONS_TEXT} ${lastVoyageDate}
										${CII_DATA.CALCULATED_PORTAL_INFO}`}
								/>
							</MDBCol>
							<MDBCol className="col-3">
								<Controller
									name="ciiExclusion.gradeWithoutExclusion"
									control={control}
									render={({ field }) => (
										<div>
											<Input
												id="attained_cii_grade_without_exclusions_input"
												disabled={!reportFlags?.ConfirmCIIEnabled}
												onChange={(e) => {
													field.onChange(e.toUpperCase());
												}}
												readOnly
												type="text"
												defaultValue={field.value}
												value={field.value}
												autoFocus={false}
												className="cii-input"
											/>
										</div>
									)}
								/>
							</MDBCol>
						</MDBRow>
						<MDBRow className="col-12 pt-3 cii-exclusion">
							<MDBCol className="col-7 p-0">
								<Label
									className="cii-exclusion-label"
									name={`${CII_DATA.GRADES_WITH_EXCLUSIONS_TEXT} ${lastVoyageDate}
									${CII_DATA.CALCULATED_PORTAL_INFO}`}
								/>
							</MDBCol>
							<MDBCol className="col-3">
								<Controller
									name="ciiExclusion.gradeWithExclusion"
									control={control}
									render={({ field }) => (
										<div>
											<Input
												id="attained_cii_grade_with_exclusions_input"
												disabled={!reportFlags?.ConfirmCIIEnabled}
												onChange={(e) => {
													field.onChange(e.toUpperCase());
												}}
												type="text"
												readOnly
												defaultValue={field.value}
												value={field.value}
												autoFocus={false}
												className="cii-input"
											/>
										</div>
									)}
								/>
							</MDBCol>
						</MDBRow>
						<MDBRow className="col-12 pt-3 cii-exclusion">
							<MDBCol className="col-7 p-0">
								<Label bold name={CII_DATA.NOTE} className="cii-exclusion" />
							</MDBCol>
						</MDBRow>
						<div className="mt-5 d-flex justify-content-center">
							<button type="submit" className="confirm-btn-danger-lg" disabled={!reportFlags?.VoyagesEnabled}>
								{CII_DATA.CONFIRM_BTN_MNL_CII_UPDATE}
							</button>
							<Button
								type="button"
								variant="secondary"
								onClick={handleClearCIIManualForm}
								className="confirm-btn-primary-lg ml-3 "
								disabled={!reportFlags?.VoyagesEnabled}
							>
								{CII_DATA.REMOVE_BTN_MNL_CII_UPDATE}
							</Button>
						</div>
					</form>
				</div>
			)}
		</div>
	);
};

export default CIIExclusionCalculation;
